import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Sparkles, Settings2 } from "lucide-react";
import { SiteProvider, useSite } from "../context/SiteContent";
import { BookingProvider, BookingButton } from "./Booking";
import { mediaUrl } from "../api";
import { ContactList, HoursTable } from "./parts";

const NAV = [
  { to: "/", label: "Startseite", end: true },
  { to: "/leistungen", label: "Leistungen" },
  { to: "/angebote", label: "Angebote" },
  { to: "/galerie", label: "Galerie" },
  { to: "/kontakt", label: "Kontakt" },
];

function Brand({ business, light }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="site-brand">
      {business?.logo_media_id
        ? <img src={mediaUrl(business.logo_media_id)} alt={business.name} className="h-10 sm:h-12 w-auto max-w-[220px] object-contain" data-testid="site-logo-image" />
        : <span className={`font-serif leading-none ${light ? "text-stone-50" : "text-[var(--brand-primary)]"}`} data-testid="site-logo-text">
            <span className="block text-xl sm:text-2xl font-semibold tracking-[0.12em] uppercase">{business?.name || "Hair By Samira"}</span>
            {business?.tagline && <span className="block text-[9px] sm:text-[10px] tracking-[0.35em] uppercase mt-1 text-[var(--brand-accent)]">{business.tagline}</span>}
          </span>}
    </Link>
  );
}

function Header() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const showLoyalty = content?.settings?.show_loyalty_link !== false;
  const links = showLoyalty ? [...NAV, { to: "/treueprogramm", label: "Treueprogramm" }] : NAV;
  return (
    <header className="sticky top-0 z-40 bg-[#FAF7F2]/85 backdrop-blur-md border-b border-stone-200/60">
      <div className="max-w-6xl mx-auto px-5 h-16 sm:h-20 flex items-center justify-between">
        <Brand business={content?.business} />
        <nav className="hidden md:flex items-center gap-8" data-testid="site-nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) => `text-sm tracking-wide transition-colors ${isActive ? "text-[var(--brand-primary)] font-semibold" : "text-stone-600 hover:text-[var(--brand-primary)]"}`}>
              {l.label}
            </NavLink>
          ))}
          <BookingButton variant="dark" className="!px-4 !py-2" testid="nav-booking-button" />
        </nav>
        <button data-testid="nav-toggle" aria-label="Menü" onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-lg text-[var(--brand-primary)]">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav className="md:hidden border-t border-stone-200/60 bg-[#FAF7F2] px-5 py-4 flex flex-col gap-1" data-testid="site-nav-mobile">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `py-3 text-base ${isActive ? "text-[var(--brand-primary)] font-semibold" : "text-stone-700"}`}>{l.label}</NavLink>
          ))}
          <BookingButton variant="dark" className="mt-3 justify-center" testid="nav-mobile-booking-button" />
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { content } = useSite();
  const b = content?.business || {};
  const s = content?.settings || {};
  return (
    <footer className="bg-[var(--brand-primary-dark)] text-stone-300 mt-20" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-5 py-14 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-5">
          <Brand business={b} light />
          {b.tagline && <p className="text-sm text-stone-400 mt-3">{b.tagline}</p>}
          {b.description && <p className="text-sm text-stone-400 mt-4 leading-relaxed line-clamp-4">{b.description}</p>}
        </div>
        <div className="md:col-span-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[var(--brand-accent)] mb-4">Kontakt</p>
          <ContactList b={b} light />
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[var(--brand-accent)] mb-4">Öffnungszeiten</p>
          <HoursTable hours={content?.hours} light />
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-stone-500">
          <span>{s.footer_text}</span>
          <div className="flex flex-wrap gap-4">
            {(s.legal_pages || []).map((p) => <Link key={p.slug} to={`/rechtliches/${p.slug}`} className="hover:text-stone-300" data-testid={`footer-legal-${p.slug}`}>{p.title}</Link>)}
            <Link to="/website-admin" className="inline-flex items-center gap-1.5 text-[var(--brand-accent-light)] hover:text-white font-semibold" data-testid="footer-admin-link"><Settings2 className="h-3.5 w-3.5" /> Website verwalten</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

const hexToRgb = (hex) => { const m = /^#?([0-9a-f]{6})$/i.exec(hex || ""); if (!m) return null; const n = parseInt(m[1], 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
const rgba = (rgb, a) => `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
const mix = (rgb, f) => rgb.map((c) => Math.round(Math.min(255, Math.max(0, f < 0 ? c * (1 + f) : c + (255 - c) * f))));
const css = (rgb) => `rgb(${rgb.join(", ")})`;

function applyBrandColors(settings) {
  const p = hexToRgb(settings?.primary_color) || [74, 29, 36];
  const a = hexToRgb(settings?.accent_color) || [197, 155, 108];
  const dark = mix(p, -0.4);
  const vars = {
    "--brand-primary": css(p), "--brand-primary-soft": css(mix(p, 0.12)), "--brand-primary-dark": css(dark),
    "--brand-primary-70": rgba(p, 0.7), "--brand-primary-30": rgba(p, 0.3), "--brand-primary-dark-90": rgba(dark, 0.9),
    "--brand-accent": css(a), "--brand-accent-light": css(mix(a, 0.35)), "--brand-accent-hover": css(mix(a, 0.12)),
    "--brand-accent-10": rgba(a, 0.1), "--brand-accent-20": rgba(a, 0.18), "--brand-accent-40": rgba(a, 0.4),
  };
  Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
}

function Shell() {
  const { content, error } = useSite();
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    if (!content) return;
    applyBrandColors(content.settings);
    document.title = content.settings?.seo_title || content.business?.name || "Hair By Samira";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = content.settings?.seo_description || "";
    document.documentElement.lang = content.settings?.language || "de";
    if (content.settings?.favicon_media_id) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = mediaUrl(content.settings.favicon_media_id);
    }
  }, [content]);

  if (error) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-stone-600" data-testid="site-error">Die Website konnte nicht geladen werden. Bitte später erneut versuchen.</div>;
  if (!content) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center" data-testid="site-loading"><div className="h-8 w-8 rounded-full border-2 border-[var(--brand-primary)] border-t-transparent animate-spin" /></div>;
  if (content.settings?.published === false) return <div className="min-h-screen bg-[#FAF7F2] flex flex-col items-center justify-center text-center px-5" data-testid="site-offline"><Sparkles className="h-6 w-6 text-[var(--brand-accent)]" /><h1 className="font-serif text-3xl text-[var(--brand-primary)] mt-4">{content.business?.name || "Hair By Samira"}</h1><p className="text-stone-600 mt-2">Unsere Website wird gerade überarbeitet. Bis bald!</p><Link to="/treueprogramm" className="mt-6 text-sm text-[var(--brand-primary)] underline">Zum Treueprogramm</Link></div>;
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-stone-900 font-sans">
      <Header />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}

export default function SiteLayout() {
  return <SiteProvider><BookingProvider><Shell /></BookingProvider></SiteProvider>;
}
